import { useCallback, useMemo, useState } from 'react';

// Fields that should default to descending order when first selected
// (dates, counts, and ranked fields where "biggest/newest first" is the
// natural reading order). Everything else defaults to ascending (A-Z, etc).
const DESCENDING_BY_DEFAULT = [
  'createdAt',
  'updatedAt',
  'detectionTime',
  'lastSeen',
  'firstSeen',
  'riskScore',
  'detectionCount',
  'sightingCount',
  'incidentCount',
  'severity',
  'priority',
  'criticality',
  'confidence',
  'tlp',
  'sla.resolveBy',
  'sla.respondBy',
  'risk',
  'status',
  'tags',
];

const parseSort = (sortParam) => {
  if (sortParam && sortParam.startsWith('-')) {
    return { field: sortParam.slice(1), direction: 'desc' };
  }
  return { field: sortParam || '', direction: 'asc' };
};

const buildSort = (field, direction) => (direction === 'desc' ? `-${field}` : field);

/**
 * Shared sort state for list pages backed by a `sort=<field>` /
 * `sort=-<field>` API param. `defaultSort` is a param-shaped string,
 * e.g. '-detectionTime'.
 *
 * Returns:
 * - sort: the current param string, e.g. '-detectionTime'
 * - field / direction: the parsed parts
 * - toggle(field): click handler for a column header - flips direction if
 *   the column is already active, otherwise selects it with a sensible
 *   default direction (desc for dates/numbers/ranked fields, asc otherwise)
 * - setSort(sortParam): set the raw param string directly (e.g. to keep a
 *   "Sort" dropdown in sync)
 */
export const useTableSort = (defaultSort) => {
  const [sort, setSort] = useState(defaultSort);

  const { field, direction } = useMemo(() => parseSort(sort), [sort]);

  const toggle = useCallback((nextField) => {
    setSort((current) => {
      const parsed = parseSort(current);
      if (parsed.field === nextField) {
        return buildSort(nextField, parsed.direction === 'asc' ? 'desc' : 'asc');
      }
      const nextDirection = DESCENDING_BY_DEFAULT.includes(nextField) ? 'desc' : 'asc';
      return buildSort(nextField, nextDirection);
    });
  }, []);

  return { sort, field, direction, toggle, setSort };
};

export default useTableSort;
